import { t } from "../i18n";
import { useMemo, useState } from "react";
import { Check, Minus, Plus, X } from "lucide-react";
import type { OrderLine, Product } from "../types";
import { encodePricedOptions, optionLabels, splitPricedOptions } from "../utils";
import { useBackHandler } from "../backNavigation";

type CorrectionResult = {
  targetQuantity: number;
  withoutOptions: string;
  extras: string;
  counters: string;
  itemComment: string;
  takeaway: boolean;
  price: number;
};

type Props = {
  line: OrderLine;
  product?: Product;
  busy: boolean;
  onCancel: () => void;
  onSave: (result: CorrectionResult) => void;
};

function splitList(value: string): string[] {
  return (value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function mergeChoices(configured: string[], selected: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const option of [...configured, ...selected]) {
    if (!seen.has(option)) {
      seen.add(option);
      result.push(option);
    }
  }
  return result;
}

function countValue(raw: string): number {
  const parsed = Number.parseInt(raw.replace(/[^0-9]/g, ""), 10);
  return Number.isFinite(parsed) ? Math.min(999, Math.max(0, parsed)) : 0;
}

function toggle(list: string[], option: string): string[] {
  return list.includes(option) ? list.filter((item) => item !== option) : [...list, option];
}

export function CorrectionDialog({ line, product, busy, onCancel, onSave }: Props) {
  const [quantity, setQuantity] = useState(line.quantity);
  const [withoutOptions, setWithoutOptions] = useState<string[]>(splitList(line.without_options));
  const [extras, setExtras] = useState<string[]>(optionLabels(line.extras));
  const [comment, setComment] = useState(line.comment || "");
  const [takeaway, setTakeaway] = useState(Boolean(line.takeaway));
  useBackHandler(true, onCancel);

  const withoutChoices = useMemo(
    () => mergeChoices(splitList(product?.without_options ?? ""), splitList(line.without_options)),
    [product, line.without_options]
  );
  const extraChoices = useMemo(
    () => mergeChoices(optionLabels(product?.extras ?? ""), optionLabels(line.extras)),
    [product, line.extras]
  );

  return (
    <div className="dialog-backdrop">
      <section className="details-dialog" role="dialog" aria-modal="true" aria-labelledby="correction-title">
        <header className="dialog-header">
          <div>
            <h2 id="correction-title">{line.name}</h2>
            <p>{t("Διόρθωση προϊόντος")}</p>
          </div>
          <button type="button" className="icon-button" onClick={onCancel} aria-label={t("Κλείσιμο")}>
            <X />
          </button>
        </header>

        <div className="dialog-scroll">
          <div className="quantity-editor main-quantity">
            <button
              type="button"
              className="icon-button"
              onClick={() => setQuantity(Math.max(0, quantity - 1))}
              aria-label={t("Μείωση")}
            >
              <Minus />
            </button>
            <label>
              <span>{t("Νέα ποσότητα")}</span>
              <input
                inputMode="numeric"
                value={quantity}
                onFocus={(event) => event.currentTarget.select()}
                onChange={(event) => setQuantity(countValue(event.target.value))}
              />
            </label>
            <button
              type="button"
              className="icon-button"
              onClick={() => setQuantity(Math.min(999, quantity + 1))}
              aria-label={t("Αύξηση")}
            >
              <Plus />
            </button>
          </div>

          {withoutChoices.length ? (
            <fieldset className="option-group">
              <legend>{t("Χωρίς")}</legend>
              <div className="check-grid">
                {withoutChoices.map((option) => (
                  <label className="check-option" key={option}>
                    <input
                      type="checkbox"
                      checked={withoutOptions.includes(option)}
                      onChange={() => setWithoutOptions(toggle(withoutOptions, option))}
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          ) : null}

          {extraChoices.length ? (
            <fieldset className="option-group">
              <legend>Extra</legend>
              <div className="check-grid">
                {extraChoices.map((option) => (
                  <label className="check-option" key={option}>
                    <input
                      type="checkbox"
                      checked={extras.includes(option)}
                      onChange={() => setExtras(toggle(extras, option))}
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          ) : null}
        </div>

        <div className="dialog-sticky-fields">
          <label className="takeaway-option">
            <input
              type="checkbox"
              checked={takeaway}
              onChange={(event) => setTakeaway(event.target.checked)}
            />
            <span>{t("Πακέτο")}</span>
          </label>
          <label className="comment-field">
            <span>{t("Σχόλιο")}</span>
            <textarea rows={2} value={comment} onChange={(event) => setComment(event.target.value)} />
          </label>
        </div>

        <footer className="dialog-actions">
          <button type="button" className="secondary-command" onClick={onCancel} disabled={busy}>
            <span>{t("Άκυρο")}</span>
          </button>
          <button
            type="button"
            className="primary-command"
            disabled={busy}
            onClick={() =>
              onSave({
                targetQuantity: quantity,
                withoutOptions: withoutOptions.join(", "),
                extras: encodePricedOptions(extras.map((label) => {
                  const configured = splitPricedOptions(product?.extras ?? "").find((option) => option.label === label);
                  const stored = splitPricedOptions(line.extras).find((option) => option.label === label);
                  return configured ?? stored ?? { label, price: 0 };
                })),
                counters: line.counters || "",
                itemComment: comment.trim(),
                takeaway,
                price: Math.max(0, line.price
                  - splitPricedOptions(line.extras).reduce((sum, option) => sum + option.price, 0)
                  + extras.reduce((sum, label) => sum + (splitPricedOptions(product?.extras ?? "").find((option) => option.label === label)?.price ?? 0), 0))
              })
            }
          >
            <Check />
            <span>{quantity === 0 ? t("Αφαίρεση") : t("Αποστολή")}</span>
          </button>
        </footer>
      </section>
    </div>
  );
}
